import {
  keepPreviousData,
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';

import {
  captureTanstackError,
  onQueryErrorHandler,
} from '@src/helpers/tanstack.helpers';
import { timeout } from '@src/helpers/timeout.helpers';

// INFO: only `queries` / `mutations` are read from defaultOptions; the caches below must be
// passed to QueryClient itself. Spread into queryClient.setDefaultOptions in auth.helpers / user.queries
export const queryClientDefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: false,
  },
};

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, variables, onMutateResult, mutation, context): void => {
      captureTanstackError({
        type: 'mutation',
        error,
        mutationKey: mutation.options.mutationKey ?? context.mutationKey,
        variables,
        onMutateResult,
      });
    },
    // INFO: keeps the mutation pending until meta.minDuration has passed since mutate();
    // runs after mutationFn and useMutation onSuccess / onError, so their requests count too
    onSettled: async (_data, _error, _variables, _onMutateResult, mutation) => {
      const minDuration = mutation.meta?.minDuration;
      if (!minDuration) return;

      const rest = minDuration - (Date.now() - mutation.state.submittedAt);
      if (rest > 0) await timeout(rest);
    },
  }),

  queryCache: new QueryCache({
    onError: onQueryErrorHandler(),
  }),

  defaultOptions: queryClientDefaultOptions,
});
